import React from "react";
import PropTypes from "prop-types";
import { makeStyles } from 'tss-react/mui';
import ContactAvatar from "./ContactAvatar";
import { formatUsPhone } from "../utils/contacts";

// Cells of the contacts list: the name with an avatar, and the phone and
// email values, which show a dash when the contact has none.

const KIND_LABEL = {
  personal: "Personal",
  internal: "Internal",
  external: "External",
};

const useStyles = makeStyles()((theme) => ({
  name: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    minWidth: 0,
  },
  text: {
    minWidth: 0,
  },
  title: {
    fontSize: 15,
    fontWeight: 500,
    lineHeight: 1.35,
    color: theme.palette.text.primary,
  },
  detail: {
    fontSize: 12.5,
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  value: {
    fontSize: 14.5,
    color: theme.palette.text.primary,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  email: {
    fontSize: 14.5,
    color: theme.palette.text.secondary,
    wordBreak: "break-all",
  },
  empty: {
    color: theme.palette.text.disabled,
  },
}));

// The second line: the kind of contact where kinds are mixed, and the company.
const detailOf = (contact, showKind) => {
  const parts = [];
  if (showKind && KIND_LABEL[contact.type]) parts.push(KIND_LABEL[contact.type]);
  const company = String(contact.company || "").trim();
  if (company !== "") parts.push(company);
  return parts.join(" · ");
};

export const ContactNameCell = ({ name, contact, showKind }) => {
  const { classes } = useStyles();
  const text = String(name || "").trim();
  const detail = detailOf(contact || {}, showKind);
  return (
    <div className={classes.name}>
      <ContactAvatar name={text} number={contact?.pid || contact?.id} image={contact?.profileImage} size={36} />
      <div className={classes.text}>
        <div className={classes.title}>{text !== "" ? text : "No name"}</div>
        {detail !== "" && <div className={classes.detail}>{detail}</div>}
      </div>
    </div>
  );
};

ContactNameCell.propTypes = {
  name: PropTypes.string,
  contact: PropTypes.object,
  showKind: PropTypes.bool,
};

export const ContactValueCell = ({ value, kind }) => {
  const { classes, cx } = useStyles();
  const text = value === null || value === undefined ? "" : String(value).trim();
  if (text === "") {
    return <span className={cx(classes.value, classes.empty)}>—</span>;
  }
  return kind === "phone"
    ? <span className={classes.value}>{formatUsPhone(text)}</span>
    : <span className={classes.email}>{text}</span>;
};

ContactValueCell.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  kind: PropTypes.oneOf(["phone", "email"]).isRequired,
};
